<template>
  <div>
    <span
      class="pulsate"
      v-show="getRTLoading.full_submission.includes(submission.id)"
    >
      ...Loading data
    </span>
    <div
      class="py-8 rounded px-6 bg-gray-100"
      v-show="!getRTLoading.full_submission.includes(submission.id)"
    >
      <!-- Award Points / See Point details -->
      <div v-if="getCurrentRT.enable_grading" class="mb-3">
        <PointsList
          class="px-4"
          :rewards="getCurrentRT.points_settings.peer_rewards"
          :pointCap="Number(getCurrentRT.point_cap)"
          :saving="savingAssessment"
          :editable="edit_assessment_feedback"
          @saveAssessment="saveDraftAssessment"
        />
      </div>
      <div
        v-show="!show_textbox"
        class="start-sm col-xs center-xs"
        :class="
          $route.query.submissionID === submission.id &&
          !$route.query.topLevelCommentID &&
          !$route.query.replyCommentID &&
          $route.query.assessment === true &&
          isFromNotificationState
            ? 'border-2 border-pinky bg-pink-100'
            : 'border-0'
        "
      >
        <div class="row no-margin middle-xs flex items-center justify-center">
          <h5 class="pl-subheading assessment-title text-center">
            Private Feedback
          </h5>
        </div>
        <p class="white-spaced-text pt-3 px-2">
          {{
            getStudentSummary.evaluation.comment ||
            'There is currently no feedback here...'
          }}
        </p>
      </div>
      <div class="w-full flex justify-between px-4 py-3">
        <div
          class="w-full h-full focus-within:border-primary rounded border border-solid"
        >
          <textbox
            :id="`assessment-textbox-${submission.id}`"
            :key="`assessment-textbox-${submission.id}`"
            :autoSave="false"
            v-show="show_textbox"
            :disabled="!hasTeacherPermissionsInCourse"
            v-model="evaluation.comment"
            :placeholder="`Write feedback for ${submission.user.name}`"
            theme="feedback-theme"
            :showSubmitButton="false"
            :keepFocus="evaluation.comment !== submission.evaluation.comment"
            @messageSent="submitAssessmentAndFeedback"
            @blur="saveDraftFeedback"
          />
        </div>
      </div>
      <!-- Automatically Generated Feedback section -->
      <div
        v-if="
          hasTeacherPermissionsInCourse &&
          $route.name !== 'walkthrough.feed' &&
          show_textbox
        "
        style="margin-bottom: 20px"
        class="mx-5"
      >
        <div class="flex">
          <div
            @click="
              collapsed_saved_feedback = !collapsed_saved_feedback;
              collapsed_suggested_feedback = true;
            "
          >
            <span class="font-semibold text-xs">
              {{ saved_feedback_collapse_message }}
            </span>
            <i v-if="collapsed_saved_feedback" class="fas fa-arrow-down fa-xs">
            </i>
            <i v-else class="fas fa-arrow-up fa-xs"></i>
          </div>
          <div class="px-12">
            <div
              @click="
                collapsed_suggested_feedback = !collapsed_suggested_feedback;
                collapsed_saved_feedback = true;
              "
            >
              <span class="font-semibold text-xs">
                {{ suggested_feedback_collapse_message }}
              </span>
              <i
                v-if="collapsed_suggested_feedback"
                class="fas fa-arrow-down fa-xs"
              ></i>
              <i v-else class="fas fa-arrow-up fa-xs"></i>
            </div>
          </div>
        </div>
        <div class="mt-4 w-full" v-if="!collapsed_saved_feedback">
          <div class="flex center items-center">
            <div class="font-semibold">Saved Feedback</div>
            <div class="mx-4 text-gray-400" @click="refreshSavedFeedback">
              <i class="fas fa-sync fa-xs" aria-hidden="true"></i>
            </div>
          </div>
          <div v-if="saved_feedback_to_display.length">
            <div
              class="flex center my-5 justify-between px-2"
              v-for="(feedback, index) in saved_feedback_to_display"
              :key="index"
            >
              <span class="font-medium">{{ feedback.text }}</span>
              <div class="flex">
                <button class="sample" @click="selectSavedFeedback(feedback)">
                  <i
                    class="fas fa-plus-circle text-2xl ml-3 text-gray-400 mx-6"
                  ></i>
                </button>
                <div class="center items-center my-2">
                  <v-dropdown placement="right">
                    <template v-slot:button>
                      <i
                        class="fas fa-ellipsis-v cursor-pointer text-gray-500 w-full flex items-center justify-end text-md"
                      />
                    </template>
                    <template v-slot:content>
                      <ul class="w-28">
                        <li
                          class="sample group px-3 py-2 rounded-t flex items-center w-full h-9 cursor-pointer hover:bg-gray-100"
                          @click="editSavedFeedback(feedback)"
                        >
                          <i class="fa fa-edit" aria-hidden="true"></i>
                          <span class="mx-3 text-xs"> Edit</span>
                        </li>

                        <li
                          class="sample group px-3 py-2 rounded-b flex items-center w-full h-9 cursor-pointer hover:bg-gray-100 text-red-600"
                          @click="deleteSavedFeedback(feedback)"
                        >
                          <i class="text-red-500 fas fa-trash-alt" />
                          <span class="mx-3 text-xs px-1"> Delete</span>
                        </li>
                      </ul>
                    </template>
                  </v-dropdown>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <div class="text-left my-5">
              You have no saved feedback templates. Create a new template or
              import some from an existing course.
            </div>
          </div>
          <div>
            <button
              class="sample mr-2 px-5 py-2 cursor-pointer text-xs font-semibold text-white bg-blueGray-500 rounded"
              @click="newSavedFeedback"
            >
              <span>New</span>
            </button>
            <button
              class="sample mr-2 px-5 py-2 cursor-pointer text-xs font-semibold text-white bg-blueGray-500 rounded"
              @click="showImportFeedback"
            >
              <span>Import</span>
            </button>
          </div>
        </div>

        <div>
          <div class="mt-4" v-if="!collapsed_suggested_feedback">
            <div class="flex font-semibold">
              Suggested Feedback
              <div class="mx-4 text-gray-400" @click="refreshAutomatedFeedback">
                <i class="fas fa-sync fa-xs" aria-hidden="true"></i>
              </div>
            </div>
            <div
              class="flex center my-5 justify-between"
              v-for="(feedback, index) in automated_feedback_to_display"
              :key="index"
            >
              <div class="">
                <span class="font-medium">{{ feedback.text }}</span>
                <br />
                <!-- <span v-my-tooltip="feedback.explanation" class="ruby pl-caption">
                  Why this suggestion?
                </span> -->
                <Tooltip
                  :text="feedback.explanation"
                  styleTooltip="top-0 -mt-1 left-full ml-2"
                  positionTooltip="absolute"
                >
                  <button class="text-pinky">Why this suggestion?</button>
                </Tooltip>
              </div>
              <div>
                <button @click="selectAutomatedFeedback(feedback)">
                  <i
                    class="fas fa-plus-circle text-2xl ml-3 text-gray-400 mx-6"
                  ></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Comment Graph and Comments made by student -->
      <div class="flex items-center p-4 cursor-default">
        <div
          class="mr-5 cursor-pointer"
          @click="collapsed_comments_made = !collapsed_comments_made"
        >
          <span class="font-semibold text-base">
            {{
              getStudentSummary.my_comments.length === 1
                ? 'Comment Made'
                : 'Comments Made'
            }}
            ({{ getStudentSummary.my_comments.length }})
          </span>
          <i v-if="collapsed_comments_made" class="fas fa-arrow-down fa-xs"></i>
          <i v-else class="fas fa-arrow-up fa-xs"></i>
        </div>
        <span class="text-xs text-gray-400">
          Class Median =
          {{ getStudentSummary.diversity_info.median_comments_submission }}
        </span>
      </div>
      <comment-summary v-if="!collapsed_comments_made" class="px-4" />
      <div v-if="hasTeacherPermissionsInCourse" class="flex justify-end px-4">
        <button
          v-if="show_textbox"
          class="sample py-2 px-5 bg-primary rounded cursor-pointer text-white font-semibold disabled:opacity-50"
          @click="submitAssessmentAndFeedback"
        >
          {{ hasAssessments ? 'Update' : 'Send' }}
        </button>
        <button
          v-else
          class="sample py-2 px-5 bg-primary rounded cursor-pointer text-white font-semibold disabled:opacity-50"
          @click.stop.prevent="
            showTextbox;
            edit_assessment_feedback = true;
          "
        >
          Edit
        </button>
      </div>
    </div>
    <NewFeedbackTemplateModal
      v-if="show_saved_feedback_modal"
      @close="closeFeedbackModal()"
      typeRoundtable="online"
      :feedbackID="feedback_id"
      :feedbackContent="feedback_content"
      :editingFeedback="set_edit_saved_feedback"
    />
    <ImportFeedbackTemplatesModal
      v-if="show_import_feedback_modal"
      @close="show_import_feedback_modal = false"
      typeRoundtable="online"
    />
    <!-- Delete online saved feedback -->
    <dialog-delete-confirm
      v-if="show_confirm_delete_feedback"
      @close="show_confirm_delete_feedback = false"
      @actionConfirmed="confirmDeleteSavedFeedback"
      :header="'Delete this saved feedback template'"
      :subHeader="'It will be permanently removed from this course.'"
    />
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import Textbox from 'components/textbox.vue';
  import CommentSummary from 'components/commentSummary/commentSummary.vue';
  import StudentCommentSummaryGraph from 'components/studentCommentSummaryGraph.vue';
  import PointDescriptions from 'components/pointDescriptions.vue';
  import $ from 'jquery';
  import { EventBus } from 'utils/event-bus';
  export default {
    data() {
      return {
        draft_evaluation: null,
        evaluation: {
          grade: 0,
          comment: '',
        },
        comments_median: 0,
        edit_assessment_feedback: false,
        feedback_loading: false,
        taken_suggestions: [],
        timeOutId: null,
        savingAssessment: false,
        comments_by_submissionID: {},
        collapsed_suggested_feedback: true,
        collapsed_saved_feedback: true,
        show_saved_feedback_modal: false,
        show_import_feedback_modal: false,
        feedback_content: '',
        feedback_id: '',
        set_edit_saved_feedback: false,
        show_confirm_delete_feedback: false,
        feedback_to_delete: {},
        saved_feedback: [],
        submittingFeedback: false,
        collapsed_comments_made: true,
        assessments_to_update: {},
      };
    },
    components: {
      PointsList: () => import('components/pointsList'),
      Textbox,
      CommentSummary,
      StudentCommentSummaryGraph,
      PointDescriptions,
      Tooltip: () => import('../../../components/Tooltip.vue'),
      ImportFeedbackTemplatesModal: () =>
        import('../../../components/modals/ImportFeedbackModal.vue'),
      NewFeedbackTemplateModal: () =>
        import('components/modals/NewFeedbackModal.vue'),
    },
    props: {
      submission: {
        type: Object,
        default() {
          return {};
        },
      },
      studentActivitySummary: {
        type: Boolean,
        default: false,
      },
    },
    watch: {
      getStudentSummary(new_val) {
        if (new_val) {
          this.taken_suggestions = new_val.taken_suggestions;
          this.saved_feedback = new_val.saved_feedback;
        }
      },
    },
    computed: {
      ...mapGetters([
        'getProfile',
        'getCurrentRT',
        'hasTeacherPermissionsInCourse',
        'getRTLoading',
        'getCourseDetail',
        'getStudentSummary',
        'summaryOverlayOpened',
        'getStudentAssessments',
      ]),
      isFromNotificationState() {
        return this.$store.state.isFromNotification;
      },
      suggested_feedback_collapse_message() {
        if (this.collapsed_suggested_feedback)
          return (
            'Suggested Feedback (' +
            this.automated_feedback_to_display.length +
            ')'
          );
        return (
          'Suggested Feedback (' +
          this.automated_feedback_to_display.length +
          ')'
        );
      },
      saved_feedback_collapse_message() {
        if (this.collapsed_saved_feedback)
          return (
            'Saved Feedback (' +
            this.saved_feedback_to_display.length + //change variable
            ')'
          );
        return (
          'Saved Feedback (' +
          this.saved_feedback_to_display.length + //change variable
          ')'
        );
      },
      automated_feedback_to_display() {
        return this.getStudentSummary.automated_feedback.filter((feedback) => {
          return !this.taken_suggestions.includes(feedback.tag);
        });
      },
      show_textbox() {
        if (!this.hasTeacherPermissionsInCourse) {
          return false;
        }
        return this.edit_assessment_feedback;
      },
      user_rewards() {
        return this.submission.rewards || [];
      },
      teacher_ids_in_course() {
        return this.getCourseDetail.users
          .filter((user) => {
            return (
              user.subscription.role === 'author' ||
              user.subscription.role === 'co-teacher'
            );
          })
          .map((teacher) => teacher.id);
      },
      unsaved_work() {
        return this.submission.evaluation.comment !== this.evaluation.comment;
      },
      saved_feedback_to_display() {
        if (this.saved_feedback && this.saved_feedback.length > 0) {
          return this.getCourseDetail.saved_online_feedback.filter(
            (feedback) => {
              return !this.saved_feedback.includes(feedback.id);
            }
          );
        } else {
          return this.getCourseDetail.saved_online_feedback;
        }
      },
      hasAssessments() {
        return (
          this.getStudentSummary.submission.assessments &&
          this.getStudentSummary.submission.assessments.length
        );
      },
    },
    methods: {
      newSavedFeedback() {
        if (!this.getProfile.is_paid) {
          const text =
            'save feedback template feature for both types of RoundTables.';
          EventBus.$emit('open-upgrade-modal', text);
          return;
        }
        if (this.summaryOverlayOpened || this.studentActivitySummary) {
          this.show_saved_feedback_modal = true;
        } else {
          this.$emit('newSavedFeedback');
        }
      },
      showImportFeedback() {
        if (!this.getProfile.is_paid) {
          const text =
            'save feedback template feature for both types of RoundTables.';
          EventBus.$emit('open-upgrade-modal', text);
          return;
        }
        if (this.summaryOverlayOpened || this.studentActivitySummary) {
          this.show_import_feedback_modal = true;
        } else {
          this.$emit('showImportFeedback');
        }
      },
      showTextbox() {
        this.edit_assessment_feedback = true;
        this.$nextTick(() => {
          $(`#assessment-textbox-${this.submission.id}`)
            .find('textarea')
            .focus();
        });
      },
      refreshAutomatedFeedback() {
        this.$store
          .dispatch('refreshAutomatedFeedback', {
            submission_id: this.submission.id,
          })
          .then(() => {
            this.taken_suggestions = [];
          });
      },
      refreshSavedFeedback() {
        return (this.saved_feedback = []);
      },
      selectAutomatedFeedback(feedback) {
        this.edit_assessment_feedback = true;
        if (this.evaluation.comment) {
          this.evaluation.comment += `\n ${feedback.text}`;
        } else {
          this.evaluation.comment = feedback.text;
        }
        this.$store
          .dispatch('selectAutomatedFeedback', {
            tag: feedback.tag,
            submission_id: this.submission.id,
          })
          .then(() => {
            this.taken_suggestions.push(feedback.tag);
          })
          .catch(console.log);
      },
      selectSavedFeedback(feedback) {
        if (!this.getProfile.is_paid) {
          const text =
            'save feedback template feature for both types of RoundTables.';
          EventBus.$emit('open-upgrade-modal', text);
          return;
        }
        this.edit_assessment_feedback = true;
        if (this.evaluation.comment) {
          this.evaluation.comment += `\n ${feedback.text}`;
        } else {
          this.evaluation.comment = feedback.text;
        }
        this.$store
          .dispatch('selectSavedFeedback', {
            feedback_id: feedback.id,
            submission_id: this.submission.id,
          })
          .then(() => {
            this.saved_feedback.push(feedback.id);
          })
          .catch(console.log);
      },
      async saveDraftAssessment(assessment) {
        const assessments = { assessments: this.mergedAssessments(assessment) };
        this.savingAssessment = true;
        this.$store
          .dispatch('saveDraftAssessment', {
            submissionID: this.submission.id,
            assessments,
          })
          .then(() => (this.savingAssessment = false))
          .catch((err) => {
            this.savingAssessment = false;
            console.error(err);
          });
        this.assessments_to_update = assessments;
      },
      mergedAssessments(assessment) {
        return this.getStudentSummary.points_settings.peer_rewards.map((pr) => {
          let value = 0;
          if (assessment.tag === pr.tag) {
            if (
              this.getCurrentRT.point_cap &&
              parseFloat(assessment.value) > this.getCurrentRT.point_cap
            ) {
              value = this.getCurrentRT.point_cap;
            } else {
              value = assessment.value;
            }
          } else {
            // If updated assessmet, set its value, if not, set assessment value of detault from peer rewards
            let existingAssessment = null;
            existingAssessment =
              this.getStudentSummary.submission.draft_assessments.find(
                (a) => a.tag === pr.tag
              );

            if (existingAssessment) {
              if (assessment.tag === existingAssessment.tag) {
                value = assessment.value;
              } else {
                value = existingAssessment.value;
              }
              if (
                this.getCurrentRT.point_cap &&
                parseFloat(value) > this.getCurrentRT.point_cap
              ) {
                value = this.getCurrentRT.point_cap;
              }
            }
          }
          return {
            tag: pr.tag,
            value,
          };
        });
      },
      removePoint(pointTag) {
        this.$store.dispatch('removeSubmissionPoint', {
          submissionID: this.submission.id,
          pointTag,
        });
      },
      saveDraftFeedback() {
        if (this.getStudentSummary.evaluation.comment) return;
        const payload = {
          submissionID: this.submission.id,
          draft_evaluation: { ...this.evaluation, auto_save: false },
        };
        this.$store.dispatch('postDraftEvaluation', payload).then(() => {});
      },
      editSavedFeedback(feedback) {
        if (!this.getProfile.is_paid) {
          const text =
            'save feedback template feature for both types of RoundTables.';
          EventBus.$emit('open-upgrade-modal', text);
          return;
        }
        this.feedback_id = feedback.id;
        this.feedback_content = feedback.text;
        if (this.summaryOverlayOpened || this.studentActivitySummary) {
          this.show_saved_feedback_modal = true;
          this.set_edit_saved_feedback = true;
        } else {
          EventBus.$emit('editSavedFeedback', {
            feedback_id: this.feedback_id,
            feedback_content: this.feedback_content,
          });
        }
      },
      deleteSavedFeedback(feedback) {
        if (!this.getProfile.is_paid) {
          const text =
            'save feedback template feature for both types of RoundTables.';
          EventBus.$emit('open-upgrade-modal', text);
          return;
        }
        this.feedback_to_delete = feedback;
        if (this.summaryOverlayOpened || this.studentActivitySummary) {
          this.show_confirm_delete_feedback = true;
        } else {
          EventBus.$emit('deleteSavedFeedback', this.feedback_to_delete);
        }
      },
      confirmDeleteSavedFeedback() {
        this.$store
          .dispatch('deleteFeedback', {
            course_id: this.getCourseDetail.id,
            feedback_id: this.feedback_to_delete.id,
            onlineRT: true,
          })
          .then(() => {
            this.feedback_to_delete = {};
            this.show_confirm_delete_feedback = false;
          });
      },
      submitAssessmentAndFeedback() {
        this.submittingFeedback = true;
        this.feedback_loading = true;
        if (this.assessments_to_update.assessments) {
          this.$store
            .dispatch('saveAssessment', {
              submissionID: this.submission.id,
              assessments: this.assessments_to_update,
            })
            .catch((err) => console.error(err));
        }
        this.$store
          .dispatch('postEvaluation', {
            submissionID: this.submission.id,
            evaluation: { ...this.evaluation, auto_save: false },
          })
          .then(() => {
            this.submittingFeedback = false;
            this.feedback_loading = false;
            this.edit_assessment_feedback = false;
            if (this.evaluation.comment !== '') {
              const toast = {
                type: 'Success',
                message: 'Feedback submitted with success!',
                time: 5000,
              };
              EventBus.$emit('createToast', toast);
            }
          });
      },
      closeFeedbackModal() {
        this.show_saved_feedback_modal = false;
        this.set_edit_saved_feedback = false;
        this.feedback_id = '';
        this.feedback_content = '';
      },
    },
    mounted() {
      this.evaluation.comment = this.getStudentSummary.evaluation.comment
        ? this.getStudentSummary.evaluation.comment
        : this.getStudentSummary.draft_evaluation;
      this.edit_assessment_feedback =
        !this.getStudentSummary.evaluation.comment &&
        !this.getStudentSummary.submission.assessments.length;
      this.taken_suggestions = this.getStudentSummary.taken_suggestions;
      this.saved_feedback = this.getStudentSummary.saved_feedback;
      this.assessments_to_update = {
        assessments: this.getStudentSummary.submission.draft_assessments,
      };
    },
    beforeDestroy() {},
  };
</script>
<style scoped lang="stylus">
  @import '~app/global-stylus.styl'
  .ruby
    color: pl-ruby
  .assessment-title
    color:#273248
    font-size: 18px
    font-weight:bold
  .suggested-feedback-div
    cursor:pointer;
    width:fit-content;
    float:right
    font-size:12px
    margin-top:5px
    &:hover
      color: pl-primary
  .suggested-saved-div
    cursor:pointer;
    width:fit-content;
    float:left
    font-size:12px
    margin-top:5px
    &:hover
      color: pl-primary
  .submission-assessment-points-list
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  .create-new-image
    margin-right: 5px
    width: 10px
    height: 10px
</style>



// WEBPACK FOOTER //
// assessmentTab.vue?4da60e3a